import type { SharedAttachment } from "./components/ops-shared-attachment-data.js";
import { OpsSourceCoveragePageSchema, WhatsAppClearResyncPreflightSchema } from "@business-ops/domain";
import type {
  AgentControlState,
  AgentBrainSettings,
  AgentProposal,
  ApproveAgentProposalBundle,
  BoardSnapshot,
  BusinessOperatingModel,
  BusinessOperatingModelVersion,
  ConversationRule,
  CreateWorkItem,
  MessageDestinationConfirmation,
  OpsAssistantPublicMutationCommand,
  OpsAssistantPublicResponse,
  OpsAssistantWorkspaceContext as DomainOpsAssistantWorkspaceContext,
  OpsAssistantWorkspaceReference as DomainOpsAssistantWorkspaceReference,
  OpsImageJobProjection,
  OpsPublicTurnEnvelope,
  OpsSha256,
  OpsSourceCoverageItem,
  OpsSourceCoveragePage,
  OpsVisualProof,
  OpsArchiveMemberIdentity,
  UpdateAgentBrainSettings,
  UpdateWorkItem,
  WorkItem,
  WorkStatus
} from "@business-ops/domain";

export interface Session {
  member: BoardSnapshot["viewer"];
  workspace: Pick<BoardSnapshot["workspace"], "id" | "name" | "slug">;
  publicReleaseId: OpsSha256;
}

export interface OpsPublicTurnHistory {
  clearedAt?: string | null;
  nextCursor?: string | null;
  scope: string;
  turns: Array<{ publicTurn: OpsPublicTurnEnvelope; promptHash: string; attachments: SharedAttachment[]; prompt?: string; createdAt: string; orderKey?: string }>;
}

export type OpsAssistantCoverageSource = OpsSourceCoverageItem;
export type OpsAssistantCoveragePage = OpsSourceCoveragePage;

export interface AgentProposalReviewPage {
  proposals: AgentProposal[];
  history: AgentProposal[];
  nextCursor: string | null;
  totalCount: number;
  hasMore: boolean;
}

export interface CompleteAgentProposalReview extends AgentProposalReviewPage {
  nextCursor: null;
  hasMore: false;
}

export type OpsAssistantWorkspaceContext = DomainOpsAssistantWorkspaceContext;
export type OpsAssistantWorkspaceReference = DomainOpsAssistantWorkspaceReference;

export interface ActivityEvent {
  id: string;
  actorType: "HUMAN" | "AGENT" | "SYSTEM";
  actorId: string | null;
  action: string;
  entityType: string;
  entityId: string;
  reason: string;
  whatsappDeliveryState: "SENT" | "DELIVERED" | "READ" | "PLAYED" | null;
  reversible: boolean;
  createdAt: string;
}

export interface WorkNote {
  id: string;
  body: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
}

export interface WhatsAppConnection {
  botName: string;
  monitoringScope: "ALL_CONVERSATIONS";
  status: "STARTING" | "CONNECTING" | "SCAN_REQUIRED" | "CONNECTED" | "DISCONNECTED" | "RECONNECTING" | "UNAVAILABLE";
  liveCaptureStatus: "NOT_CONNECTED" | "UNPROVEN" | "PROVEN";
  lastLiveBatchAt: string | null;
  captureGeneration?: number | null;
  completeThrough?: string | null;
  coverageGap?: boolean;
  qrDataUrl: string | null;
  qrExpiresAt: string | null;
  authorizedGroupConfigured: boolean;
  authorizedGroup: {
    groupJid: string;
    name: string;
    participantCount: number | null;
  } | null;
  observeOnly: boolean;
  canReconnect: boolean;
  updatedAt: string;
}

export type GoogleWorkspaceCapability =
  | "SELECTED_FILE_READ"
  | "MEET_TRANSCRIPT_READ"
  | "CALENDAR_CONTEXT_READ"
  | "BROAD_DRIVE_DISCOVERY"
  | "CALENDAR_OWNED_EVENT_WRITE";

export interface GoogleWorkspaceSettings {
  state: "UNAVAILABLE" | "DISCONNECTED" | "CONNECTING" | "CHECK_REQUIRED" | "REAUTH_REQUIRED" | "SCOPE_UPGRADE_REQUIRED" | "CONNECTED";
  account: { email: string } | null;
  grantedCapabilities: readonly GoogleWorkspaceCapability[];
  missingCapabilities: readonly GoogleWorkspaceCapability[];
  broadDriveDiscovery: "NOT_REQUESTED" | "AVAILABLE" | "REQUIRES_RESTRICTED_SCOPE";
  canConnect: boolean;
  canDisconnect: boolean;
  unavailableReason: string | null;
  updatedAt: string | null;
}

export interface WhatsAppCaptureSummary {
  conversationCount: number;
  groupCount: number;
  directCount: number;
  broadcastCount: number;
  messageCount: number;
  liveMessageCount: number;
  historyMessageCount: number;
  oldestMessageAt: string | null;
  newestMessageAt: string | null;
}

export interface WhatsAppSearchResult {
  sourceEventId: string;
  conversationLabel: string;
  conversationKind: "GROUP" | "DIRECT" | "BROADCAST" | "UNKNOWN";
  senderLabel: string;
  occurredAt: string;
  kind: "TEXT" | "VOICE_TRANSCRIPT";
  content: string;
}

export interface WhatsAppBrowserConversation {
  id: string;
  label: string;
  kind: "GROUP" | "DIRECT" | "BROADCAST";
  lastMessageAt: string | null;
  messageCount: number;
  historySyncObserved: boolean;
  participantCount: number;
  client: { id: string; name: string } | null;
  person: { id: string; name: string } | null;
  latestMessage: {
    senderLabel: string;
    occurredAt: string;
    preview: string;
    mediaKind: "IMAGE" | "VIDEO" | "AUDIO" | "DOCUMENT" | null;
  } | null;
}

export interface SummaryDeliveryPreference {
  workspaceId: string;
  version: number;
  enabled: boolean;
  group: { id: string; label: string } | null;
  frequency: "ONCE_DAILY" | "THREE_DAILY";
  timezone: string;
  localTimes: string[];
}
export interface SummaryDeliveryUpdate {
  expectedVersion: number;
  enabled: boolean;
  groupId: string | null;
  frequency: SummaryDeliveryPreference["frequency"];
  confirmedGroupId: string | null;
  confirmedGroupLabel: string | null;
  confirmGroupMembersReceiveSummary: boolean;
}

export interface WhatsAppBrowserAttachment {
  mediaKind: "IMAGE" | "VIDEO" | "AUDIO" | "DOCUMENT";
  state: "PENDING" | "DOWNLOADING" | "DOWNLOADED" | "INSPECTING" | "READY" | "NEEDS_BACKFILL" | "SKIPPED" | "FAILED" | "UNSUPPORTED";
  inspectionKind: "NONE" | "TEXT" | "VISION" | "TRANSCRIPT" | "MIXED";
  inspectionText: string | null;
  fileName: string | null;
  mimeType: string | null;
  byteCount: number | null;
  durationSeconds: number | null;
  available: boolean;
  statusCode: string | null;
}

export interface WhatsAppBrowserMessage {
  id: string;
  senderLabel: string;
  fromBusiness: boolean;
  occurredAt: string;
  ingestionKind: "LIVE" | "HISTORY";
  kind: "TEXT" | "VOICE_TRANSCRIPT" | "IMAGE" | "VIDEO" | "AUDIO" | "DOCUMENT";
  content: string;
  reaction: {
    text: string | null;
    target: { kind: "AVAILABLE"; sourceEventId: string } | { kind: "UNAVAILABLE" };
  } | null;
  replyTo: {
    sourceEventId: string | null;
    senderLabel: string;
    occurredAt: string | null;
    content: string;
    mediaKind: "IMAGE" | "VIDEO" | "AUDIO" | "DOCUMENT" | null;
  } | null;
  attachment: WhatsAppBrowserAttachment | null;
  workOutcomes: Array<{
    kind: "READY_FOR_REVIEW" | "PROCESSING" | "CREATED" | "UPDATED" | "NOT_APPLIED" | "LINKED";
    proposalId: string | null;
    workItem: { id: string; title: string; status: WorkStatus } | null;
  }>;
  workOutcome: {
    kind: "READY_FOR_REVIEW" | "PROCESSING" | "CREATED" | "UPDATED" | "NOT_APPLIED" | "LINKED";
    proposalId: string | null;
    workItem: { id: string; title: string; status: WorkStatus } | null;
  } | null;
}

export interface WhatsAppBrowserConversationPage {
  conversations: WhatsAppBrowserConversation[];
  nextCursor: string | null;
  totalCount: number;
}

export interface WhatsAppBrowserThreadPage {
  conversation: WhatsAppBrowserConversation;
  messages: WhatsAppBrowserMessage[];
  nextCursor: string | null;
}

export type WhatsAppProofReplyContext =
  | { kind: "NOT_A_REPLY" }
  | { kind: "ORIGINAL_UNAVAILABLE" }
  | {
      kind: "AVAILABLE";
      sourceEventId: string;
      senderLabel: string;
      occurredAt: string;
      content: string;
    };

export interface WhatsAppPerson {
  id: string;
  displayName: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  messageCount: number;
  memberId: string | null;
  memberDisplayName: string | null;
}

export interface MeetingSession {
  id: string;
  title: string;
  state: "RECORDING" | "STOPPED" | "PROCESSING" | "READY_FOR_REVIEW" | "FAILED" | "CANCELLED";
  summary: string | null;
  clarifications: readonly string[];
  sourceEventIds: readonly string[];
  proposalIds: readonly string[];
  segmentCount: number;
  readySegmentCount: number;
  safeErrorCode: string | null;
  startedAt: string;
  stoppedAt: string | null;
  completedAt: string | null;
  version: number;
}

export interface OpsUploadedFile {
  attachments: SharedAttachment[];
  sourceEventId: string;
  fileName: string;
  mediaKind: "IMAGE" | "VIDEO" | "DOCUMENT";
  status: "READY" | "RETAINED";
}

export interface WhatsAppConversationRule {
  conversationId: string;
  label: string;
  kind: "GROUP" | "DIRECT" | "BROADCAST";
  lastMessageAt: string | null;
  messageCount: number;
  rule: ConversationRule;
  clientId: string | null;
  policyVersion: number;
  editable: boolean;
}

export type OpsAssistantMutationCommand = OpsAssistantPublicMutationCommand;

export type OpsAssistantResponse = OpsAssistantPublicResponse;

// WHY [BOL-OPS-NATIVE-HISTORY-FRAMING-001]: response kind is model-visible
// conversation state. Carry a canonical kind when one exists; legacy absence
// stays absent so browser code never guesses what Luna meant.
export type OpsAssistantHistoryMessage =
  | { role: "USER"; text: string }
  | { role: "ASSISTANT"; kind?: OpsAssistantPublicResponse["kind"]; text: string };

type OpsPublicTurnExpected = Pick<
  OpsPublicTurnEnvelope,
  "turnId" | "requestId" | "revision" | "releaseId" | "resultHash"
>;

function projectOpsPublicTurnExpected(turn: OpsPublicTurnExpected): OpsPublicTurnExpected {
  // A structural TypeScript Pick does not remove runtime envelope fields.
  // Project the exact server CAS identity so strict request parsing cannot be
  // bypassed or reject a valid full envelope supplied by the active turn.
  return {
    turnId: turn.turnId,
    requestId: turn.requestId,
    revision: turn.revision,
    releaseId: turn.releaseId,
    resultHash: turn.resultHash
  };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string
  ) {
    super(message);
  }
}

export const AUTH_REQUIRED_EVENT = "business-ops:auth-required";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers
    }
  });
  if (response.status === 204) return undefined as T;
  const payload = await response.json() as T | { error?: { code?: string; message?: string } };
  if (!response.ok) {
    const error = "error" in (payload as object) ? (payload as { error?: { code?: string; message?: string } }).error : undefined;
    const apiError = new ApiError(response.status, error?.code ?? "REQUEST_FAILED", error?.message ?? "That request did not work.");
    // WHY [BOL-SESSION-SHELL-001]: React can retain a previously authenticated
    // shell after the server has rejected its session. A child toast saying
    // "Please sign in" while the owner avatar remains visible is a lying UI.
    // Invalidate the whole shell on the first authenticated 401; Ask Ops saves
    // its transcript and draft before this event can unmount it.
    if (response.status === 401 && !["/api/auth/login", "/api/session"].includes(path) && typeof window !== "undefined") {
      window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));
    }
    throw apiError;
  }
  return payload as T;
}

async function requestBinary<T>(path: string, body: Blob, headers: HeadersInit, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers,
    body,
    ...(signal ? { signal } : {})
  });
  const payload = await response.json() as T | { error?: { code?: string; message?: string } };
  if (!response.ok) {
    const error = "error" in (payload as object) ? (payload as { error?: { code?: string; message?: string } }).error : undefined;
    if (response.status === 401 && typeof window !== "undefined") window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));
    throw new ApiError(response.status, error?.code ?? "REQUEST_FAILED", error?.message ?? "That file did not upload.");
  }
  return payload as T;
}

function mutationHeaders(requestKey: string = crypto.randomUUID()): HeadersInit {
  return { "Idempotency-Key": requestKey };
}

const REVIEW_PAGE_SIZE = 50;

function reviewPagePath(input: { cursor?: string; limit?: number } = {}): string {
  const query = new URLSearchParams();
  query.set("limit", String(input.limit ?? REVIEW_PAGE_SIZE));
  if (input.cursor) query.set("cursor", input.cursor);
  return `/api/review?${query.toString()}`;
}

async function requestReviewPage(
  input: { cursor?: string; limit?: number } = {}
): Promise<AgentProposalReviewPage> {
  return request<AgentProposalReviewPage>(reviewPagePath(input));
}

export async function readCompleteAgentProposalReview(): Promise<CompleteAgentProposalReview> {
  const proposals: AgentProposal[] = [];
  const observedProposalIds = new Set<string>();
  const observedCursors = new Set<string>();
  let history: AgentProposal[] = [];
  let cursor: string | undefined;
  let totalCount: number | null = null;
  do {
    const page = await requestReviewPage({ ...(cursor ? { cursor } : {}) });
    if (totalCount === null) {
      totalCount = page.totalCount;
      history = page.history;
    } else if (page.totalCount !== totalCount) {
      throw new Error("Approvals changed while Review was loading. Refresh and try again.");
    }
    if (!Number.isSafeInteger(page.totalCount) || page.totalCount < 0
      || page.hasMore !== Boolean(page.nextCursor)
      || (page.hasMore && page.proposals.length === 0)) {
      throw new Error("Review returned an incomplete approval page. Refresh and try again.");
    }
    for (const proposal of page.proposals) {
      if (observedProposalIds.has(proposal.id)) {
        throw new Error("Review repeated an approval page. Refresh and try again.");
      }
      observedProposalIds.add(proposal.id);
      proposals.push(proposal);
    }
    if (proposals.length > page.totalCount) {
      throw new Error("Review returned more approvals than its exact total. Refresh and try again.");
    }
    if (!page.nextCursor) break;
    if (observedCursors.has(page.nextCursor)) {
      throw new Error("Review repeated an approval cursor. Refresh and try again.");
    }
    observedCursors.add(page.nextCursor);
    cursor = page.nextCursor;
  } while (true);
  if (proposals.length !== (totalCount ?? 0)) {
    throw new Error("Review did not return every current approval. Refresh and try again.");
  }
  return {
    proposals,
    history,
    totalCount: totalCount ?? 0,
    hasMore: false,
    nextCursor: null
  };
}

export const api = {
  session: () => request<Session>("/api/session"),
  login: (email: string, password: string) => request<Session>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password })
  }),
  logout: () => request<void>("/api/auth/logout", { method: "POST" }),
  board: () => request<BoardSnapshot>("/api/board"),
  agentControl: () => request<AgentControlState>("/api/agent/control"),
  updateAgentControl: (selection: "MANUAL" | "AUTO", expectedVersion: number) => request<AgentControlState>("/api/agent/control", {
    method: "PUT",
    headers: mutationHeaders(),
    body: JSON.stringify({ selection, expectedVersion })
  }),
  agentBrain: () => request<AgentBrainSettings>("/api/agent/brain"),
  updateAgentBrain: (update: UpdateAgentBrainSettings) => request<AgentBrainSettings>("/api/agent/brain", {
    method: "PUT",
    headers: mutationHeaders(),
    body: JSON.stringify(update)
  }),
  googleWorkspaceSettings: () => request<GoogleWorkspaceSettings>("/api/google-workspace/settings"),
  connectGoogleWorkspace: () => request<{ authorizationUrl: string }>("/api/google-workspace/connect", {
    method: "POST",
    headers: mutationHeaders()
  }),
  disconnectGoogleWorkspace: () => request<GoogleWorkspaceSettings>("/api/google-workspace/disconnect", {
    method: "POST",
    headers: mutationHeaders()
  }),
  askOps: (
    text: string,
    history: readonly OpsAssistantHistoryMessage[] = [],
    workspaceContext: OpsAssistantWorkspaceContext | null = null,
    requestKey?: string
  ) => request<OpsPublicTurnEnvelope>("/api/ops-assistant", {
    method: "POST",
    headers: mutationHeaders(requestKey),
    body: JSON.stringify({ text, history, workspaceContext })
  }),
  opsHistory: (signal?: AbortSignal) => request<OpsPublicTurnHistory>("/api/ops-assistant/history", { ...(signal ? { signal } : {}) }),
  opsHistoryAll: (cursor?: string | null, signal?: AbortSignal) => request<OpsPublicTurnHistory>(
    `/api/ops-assistant/history?all=1${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`, { ...(signal ? { signal } : {}) }),
  opsFeedClear: () => request<{ clearedAt: string }>("/api/ops-assistant/feed/clear", { method: "POST" }),
  settleOpsPublicTurn: (expected: OpsPublicTurnExpected) => request<OpsPublicTurnEnvelope>(
    `/api/ops-assistant/turns/${encodeURIComponent(expected.turnId)}/settle`,
    {
      method: "POST",
      headers: mutationHeaders(),
      body: JSON.stringify(projectOpsPublicTurnExpected(expected))
    }
  ),
  rejectOpsPublicTurn: (expected: OpsPublicTurnExpected) => request<OpsPublicTurnEnvelope>(
    `/api/ops-assistant/turns/${encodeURIComponent(expected.turnId)}/reject`,
    {
      method: "POST",
      headers: mutationHeaders(),
      body: JSON.stringify(projectOpsPublicTurnExpected(expected))
    }
  ),
  adoptOpsPublicTurnProposal: (
    expected: OpsPublicTurnExpected,
    proposalId: string,
    approval: {
      expectedVersion: number;
      revision: number;
      destinationConfirmation: MessageDestinationConfirmation;
    },
    requestKey?: string
  ) => request<OpsPublicTurnEnvelope>(
    `/api/ops-assistant/turns/${encodeURIComponent(expected.turnId)}/proposals/${encodeURIComponent(proposalId)}/adopt`,
    {
      method: "POST",
      headers: mutationHeaders(requestKey),
      body: JSON.stringify({ publicTurn: projectOpsPublicTurnExpected(expected), approval })
    }
  ),
  uploadOpsFile: (file: File, requestKey?: string, signal?: AbortSignal) => requestBinary<OpsUploadedFile>(
    "/api/ops-assistant/file",
    file,
    {
      "Content-Type": "application/octet-stream",
      "Idempotency-Key": requestKey ?? crypto.randomUUID(),
      "X-Business-Ops-File-Name": encodeURIComponent(file.name),
      "X-Business-Ops-File-Type": file.type || "application/octet-stream"
    },
    signal
  ),
  cancelOpsTextTurn: (turnId: string) => request<{
    turnId: string;
    outcome: "CANCELLED" | "COMPLETED" | "NOT_ACTIVE";
  }>(`/api/ops-assistant/turns/${encodeURIComponent(turnId)}/cancel`, {
    method: "POST"
  }),
  opsTurnStatus: (turnId: string) => request<{
    turnId: string;
    outcome: "RUNNING" | "COMPLETED" | "NOT_ACTIVE";
    attachments?: SharedAttachment[];
    publicTurn?: OpsPublicTurnEnvelope;
  }>(`/api/ops-assistant/turns/${encodeURIComponent(turnId)}/status`),
  opsAssistantCoverage: (turnId: string, offset = 0, signal?: AbortSignal, includeUnavailableReasons = false) => request<OpsAssistantCoveragePage>(
    `/api/ops-assistant/turns/${encodeURIComponent(turnId)}/coverage?offset=${encodeURIComponent(String(offset))}${includeUnavailableReasons ? "&reasons=1" : ""}`,
    { ...(signal ? { signal } : {}) }
  ).then((page) => OpsSourceCoveragePageSchema.parse(page)),
  opsAssistantSourceFileUrl: (sourceEventId: string, visual?: OpsVisualProof, archiveMember?: OpsArchiveMemberIdentity) => {
    const path = `/api/ops-assistant/sources/${encodeURIComponent(sourceEventId)}/file`;
    if (!visual && !archiveMember) return path;
    if (archiveMember) {
      if (!/^[a-f0-9]{64}$/u.test(archiveMember.parentOriginalSha256)
        || !/^[a-f0-9]{64}$/u.test(archiveMember.memberSha256)
        || !Number.isSafeInteger(archiveMember.parentOriginalBytes) || archiveMember.parentOriginalBytes < 1
        || !Number.isSafeInteger(archiveMember.memberIndex) || archiveMember.memberIndex < 0 || !archiveMember.path) {
        throw new Error("archive_member_proof_invalid");
      }
      const query = new URLSearchParams({ expectedOriginalSha256: archiveMember.parentOriginalSha256,
        archiveMemberIndex: String(archiveMember.memberIndex), archiveMemberPath: archiveMember.path,
        archiveMemberSha256: archiveMember.memberSha256, archiveOriginalBytes: String(archiveMember.parentOriginalBytes) });
      return `${path}?${query.toString()}${visual?.pageNumber === null || visual?.pageNumber === undefined ? "" : `#page=${visual.pageNumber}`}`;
    }
    if (!visual) return path;
    if (!/^[a-f0-9]{64}$/u.test(visual.originalSha256)
      || (visual.pageNumber !== null && (!Number.isSafeInteger(visual.pageNumber) || visual.pageNumber < 1))) {
      throw new Error("source_file_proof_invalid");
    }
    return `${path}?expectedOriginalSha256=${visual.originalSha256}${visual.pageNumber === null ? "" : `#page=${visual.pageNumber}`}`;
  },
  upscaleOpsImage: (artifactId: string, factor: 2 | 4, requestKey?: string) => request<OpsImageJobProjection>(
    `/api/ops-assistant/images/${encodeURIComponent(artifactId)}/upscale`,
    {
      method: "POST",
      headers: mutationHeaders(requestKey),
      body: JSON.stringify({ factor })
    }
  ),
  applyOpsMutation: (
    command: OpsAssistantMutationCommand,
    requestKey: string,
    publicTurn: OpsPublicTurnExpected
  ) => request<{ text: string; workItemId: string | null; proposalId: string | null; publicTurn: OpsPublicTurnEnvelope }>("/api/ops-assistant/apply", {
    method: "POST",
    headers: mutationHeaders(requestKey),
    body: JSON.stringify({ command, publicTurn: projectOpsPublicTurnExpected(publicTurn) })
  }),
  prepareOpsWhatsAppDraft: (
    command: Extract<OpsAssistantMutationCommand, { action: "DRAFT_WHATSAPP" }>,
    requestKey?: string
  ) => request<AgentProposal>("/api/ops-assistant/prepare-whatsapp", {
    method: "POST",
    headers: mutationHeaders(requestKey),
    body: JSON.stringify(command)
  }),
  startMeeting: (title?: string, requestKey?: string) => request<MeetingSession>("/api/meetings", {
    method: "POST",
    headers: mutationHeaders(requestKey),
    body: JSON.stringify(title ? { title } : {})
  }),
  latestMeeting: () => request<{ meeting: MeetingSession | null }>("/api/meetings/latest"),
  meeting: (id: string) => request<MeetingSession>(`/api/meetings/${id}`),
  uploadMeetingSegment: (
    id: string,
    ordinal: number,
    audio: Blob,
    requestKey?: string
  ) => requestBinary<MeetingSession>(`/api/meetings/${id}/segments`, audio, {
    "Content-Type": "application/octet-stream",
    "Idempotency-Key": requestKey ?? crypto.randomUUID(),
    "X-Business-Ops-Meeting-Ordinal": String(ordinal),
    "X-Business-Ops-Audio-Type": audio.type || "audio/webm"
  }),
  stopMeeting: (id: string, requestKey?: string) => request<MeetingSession>(`/api/meetings/${id}/stop`, {
    method: "POST",
    headers: mutationHeaders(requestKey)
  }),
  cancelMeeting: (id: string, requestKey?: string) => request<MeetingSession>(`/api/meetings/${id}/cancel`, {
    method: "POST",
    headers: mutationHeaders(requestKey)
  }),
  reviewPage: (input: { cursor?: string; limit?: number } = {}) => requestReviewPage(input),
  // READY is published only after every server cursor reconciles with one
  // exact total. Compact Work/Today presentation can then filter or group the
  // complete vector without quietly turning page one into the whole queue.
  review: () => readCompleteAgentProposalReview(),
  rejectedProposals: () => request<{ proposals: AgentProposal[] }>("/api/review/rejected"),
  proposal: (id: string) => request<AgentProposal>(`/api/review/${id}`),
  routeProposal: (id: string, input: { expectedVersion: number; conversationId: string; expectedPolicyVersion: number }) => request<AgentProposal>(`/api/review/${id}/route`, {
    method: "POST",
    headers: mutationHeaders(),
    body: JSON.stringify(input)
  }),
  editProposal: (id: string, input: { expectedVersion: number; effect?: AgentProposal["effect"]; messageDraft?: string | null }) => request<AgentProposal>(`/api/review/${id}`, {
    method: "PATCH",
    headers: mutationHeaders(),
    body: JSON.stringify(input)
  }),
  editScheduledProposal: (id: string, input: {
    expectedVersion: number;
    revision: number;
    localDateTime: string;
    utcOffset?: string;
    messageDraft: string;
  }) => request<AgentProposal>(`/api/review/${id}/schedule`, {
    method: "PATCH",
    headers: mutationHeaders(),
    body: JSON.stringify(input)
  }),
  cancelScheduledProposal: (id: string, expectedVersion: number, revision: number) => request<AgentProposal>(`/api/review/${id}/schedule/cancel`, {
    method: "POST",
    headers: mutationHeaders(),
    body: JSON.stringify({ expectedVersion, revision })
  }),
  attachProposalFile: (id: string, expectedVersion: number, file: File, requestKey?: string) => requestBinary<AgentProposal>(
    `/api/review/${id}/attachment`,
    file,
    {
      "Content-Type": "application/octet-stream",
      "Idempotency-Key": requestKey ?? crypto.randomUUID(),
      "X-Business-Ops-Proposal-Version": String(expectedVersion),
      "X-Business-Ops-File-Name": encodeURIComponent(file.name),
      "X-Business-Ops-File-Type": file.type || "application/octet-stream"
    }
  ),
  attachProposalSourceFile: (id: string, expectedVersion: number, sourceEventId: string) => request<AgentProposal>(
    `/api/review/${id}/attachment/source`,
    {
      method: "POST",
      headers: mutationHeaders(),
      body: JSON.stringify({ expectedVersion, sourceEventId })
    }
  ),
  approveProposal: (
    id: string,
    expectedVersion: number,
    revision: number,
    destinationConfirmation: MessageDestinationConfirmation | null,
    requestKey?: string
  ) => request<AgentProposal>(`/api/review/${id}/approve`, {
    method: "POST",
    headers: mutationHeaders(requestKey),
    body: JSON.stringify({ expectedVersion, revision, destinationConfirmation })
  }),
  approveProposalBundle: (
    bundleId: string,
    approvals: ApproveAgentProposalBundle["approvals"],
    requestKey?: string
  ) => request<{ proposals: AgentProposal[] }>(`/api/review/bundles/${bundleId}/approve`, {
    method: "POST",
    headers: mutationHeaders(requestKey),
    body: JSON.stringify({ approvals })
  }),
  rejectProposal: (id: string, expectedVersion: number, reason: string, note = "") => request<AgentProposal>(`/api/review/${id}/reject`, {
    method: "POST",
    headers: mutationHeaders(),
    body: JSON.stringify({ expectedVersion, reason, note })
  }),
  restoreRejectedProposal: (id: string, expectedVersion: number) => request<AgentProposal>(`/api/review/${id}/restore`, {
    method: "POST",
    headers: mutationHeaders(),
    body: JSON.stringify({ expectedVersion })
  }),
  snoozeProposal: (id: string, expectedVersion: number, until: string) => request<AgentProposal>(`/api/review/${id}/snooze`, {
    method: "POST",
    headers: mutationHeaders(),
    body: JSON.stringify({ expectedVersion, until })
  }),
  whatsAppConnection: () => request<WhatsAppConnection>("/api/whatsapp/connection"),
  whatsAppCaptureSummary: () => request<WhatsAppCaptureSummary>("/api/whatsapp/capture-summary"),
  summaryDeliveryPreference: () => request<SummaryDeliveryPreference>("/api/summary-delivery-preference"),
  updateSummaryDeliveryPreference: (input: SummaryDeliveryUpdate, requestKey: string) => request<SummaryDeliveryPreference>("/api/summary-delivery-preference", {
    method: "PUT", headers: mutationHeaders(requestKey), body: JSON.stringify(input)
  }),
  whatsAppConversations: () => request<{ conversations: WhatsAppConversationRule[] }>("/api/whatsapp/conversations"),
  updateWhatsAppConversationRule: (
    id: string,
    input: { expectedVersion: number; rule: ConversationRule; clientId: string | null }
  ) => request<{ conversation: WhatsAppConversationRule }>(`/api/whatsapp/conversations/${id}/rule`, {
    method: "PUT",
    headers: mutationHeaders(),
    body: JSON.stringify(input)
  }),
  searchWhatsApp: (query: string) => request<{ results: WhatsAppSearchResult[] }>(`/api/whatsapp/search?q=${encodeURIComponent(query)}`),
  whatsAppBrowserConversations: (input: {
    query?: string;
    kind?: "GROUP" | "DIRECT" | "BROADCAST";
    cursor?: string;
    limit?: number;
  } = {}) => request<WhatsAppBrowserConversationPage>(`/api/whatsapp/browser/conversations?${browserQuery(input)}`),
  whatsAppBrowserThread: (id: string, input: { cursor?: string; limit?: number } = {}) =>
    request<WhatsAppBrowserThreadPage>(`/api/whatsapp/browser/conversations/${id}/messages?${browserQuery(input)}`),
  whatsAppBrowserMessageContext: (sourceEventId: string) =>
    request<WhatsAppBrowserThreadPage>(`/api/whatsapp/browser/messages/${encodeURIComponent(sourceEventId)}/context`),
  whatsAppProofReplyContext: (sourceEventId: string, signal?: AbortSignal) =>
    request<WhatsAppProofReplyContext>(
      `/api/whatsapp/browser/messages/${encodeURIComponent(sourceEventId)}/proof-reply`,
      signal ? { signal } : {}
    ),
  whatsAppBrowserAttachmentUrl: (sourceEventId: string) =>
    `/api/whatsapp/browser/messages/${encodeURIComponent(sourceEventId)}/attachment`,
  sourceAttachmentUrl: (sourceEventId: string) =>
    `/api/sources/${encodeURIComponent(sourceEventId)}/attachment`,
  whatsAppPeople: () => request<{ people: WhatsAppPerson[] }>("/api/whatsapp/people"),
  reconnectWhatsApp: () => request<{ accepted: true }>("/api/whatsapp/reconnect", {
    method: "POST",
    headers: mutationHeaders()
  }),
  whatsAppClearResyncPreflight: async () => WhatsAppClearResyncPreflightSchema.parse(
    await request<unknown>("/api/settings/whatsapp-clear-resync")
  ),
  linkWhatsAppPerson: (id: string, memberId: string) => request<{ person: WhatsAppPerson }>(`/api/whatsapp/people/${id}/link`, {
    method: "POST",
    headers: mutationHeaders(),
    body: JSON.stringify({ memberId })
  }),
  rejectWhatsAppIdentityRecommendation: (id: string, memberId: string, sourceEventId: string) => request<{ rejected: true }>(`/api/whatsapp/people/${id}/recommendation/reject`, {
    method: "POST",
    headers: mutationHeaders(),
    body: JSON.stringify({ memberId, sourceEventId })
  }),
  confirmWhatsAppIdentityRecommendation: (id: string, memberId: string, sourceEventId: string) => request<{ confirmed: true }>(`/api/whatsapp/people/${id}/recommendation/confirm`, {
    method: "POST",
    headers: mutationHeaders(),
    body: JSON.stringify({ memberId, sourceEventId })
  }),
  unlinkWhatsAppPerson: (id: string) => request<{ person: WhatsAppPerson }>(`/api/whatsapp/people/${id}/link`, {
    method: "DELETE",
    headers: mutationHeaders()
  }),
  createWork: (input: CreateWorkItem) => request<WorkItem>("/api/work-items", {
    method: "POST",
    headers: mutationHeaders(),
    body: JSON.stringify(input)
  }),
  updateWork: (id: string, input: UpdateWorkItem) => request<WorkItem>(`/api/work-items/${id}`, {
    method: "PATCH",
    headers: mutationHeaders(),
    body: JSON.stringify(input)
  }),
  addNote: (id: string, body: string) => request<{ id: string; body: string; createdAt: string }>(`/api/work-items/${id}/notes`, {
    method: "POST",
    headers: mutationHeaders(),
    body: JSON.stringify({ body })
  }),
  notes: (id: string) => request<{ notes: WorkNote[] }>(`/api/work-items/${id}/notes`),
  activity: () => request<{ events: ActivityEvent[] }>("/api/activity"),
  undoActivity: (id: string) => request<{ action: string; entityType: string; entityId: string }>(`/api/activity/${id}/undo`, {
    method: "POST",
    headers: mutationHeaders()
  }),
  businessModel: () => request<BusinessOperatingModelVersion>("/api/business-model"),
  // An authenticated first entry may initialize the truthful starter without
  // claiming that the owner clicked Save. The server still owns role, workspace,
  // version-0 CAS, audit, and eligible policy activation.
  initializeStarterBusinessModel: (model: BusinessOperatingModel) => request<BusinessOperatingModelVersion>("/api/business-model", {
    method: "PUT",
    headers: mutationHeaders(),
    body: JSON.stringify({ model, reason: "Truthful starter initialized at first authenticated workspace entry.", expectedVersion: 0 })
  }),
  // WHY [BOL-FIRST-MODEL-CAS-001]: Creation is only for a confirmed absent model.
  // Settings refinement supplies the exact canonical version it displayed.
  // The server fences both under the workspace lock; never retry a conflict
  // as an update or infer a newer version from the local draft.
  // WHY [BOL-SELF-SERVE-WEB-003]: the client never grants agent or model access.
  // For an eligible new business (trial or direct beta, untouched default
  // policy) the SERVER turns Luna on with the Agency's policy inside this same
  // audited save (human decision R769, "it's the same system"); callers reread
  // GET /api/agent/brain to learn the result. A 409 LUNA_TEMPLATE_UNAVAILABLE
  // means nothing was saved and the save may be retried.
  createFirstBusinessModel: (model: BusinessOperatingModel, expectedVersion = 0) => request<BusinessOperatingModelVersion>("/api/business-model", {
    method: "PUT",
    headers: mutationHeaders(),
    body: JSON.stringify({ model, reason: expectedVersion === 0 ? "Owner confirmed initial Ops role and business identity." : "Business details refined in Settings.", expectedVersion })
  })
};

export function safeGoogleAuthorizationUrl(value: string): string | null {
  try {
    const url = new URL(value);
    const expectedParameters = [
      "access_type", "client_id", "code_challenge", "code_challenge_method",
      "include_granted_scopes", "nonce", "prompt", "redirect_uri",
      "response_type", "scope", "state"
    ];
    const parameterNames = [...url.searchParams.keys()];
    const expectedScopes = new Set([
      "openid",
      "email",
      "https://www.googleapis.com/auth/drive.file",
      "https://www.googleapis.com/auth/meetings.space.readonly",
      "https://www.googleapis.com/auth/calendar.events.readonly"
    ]);
    const scopes = (url.searchParams.get("scope") ?? "").split(/\s+/u).filter(Boolean);
    const safe = url.protocol === "https:"
      && url.hostname === "accounts.google.com"
      && url.port === ""
      && url.username === ""
      && url.password === ""
      && url.pathname === "/o/oauth2/v2/auth"
      && url.searchParams.get("response_type") === "code"
      && url.searchParams.get("access_type") === "offline"
      && url.searchParams.get("include_granted_scopes") === "true"
      && url.searchParams.get("prompt") === "consent"
      && url.searchParams.get("code_challenge_method") === "S256"
      && expectedParameters.every((name) => Boolean(url.searchParams.get(name))
        && url.searchParams.getAll(name).length === 1)
      && parameterNames.length === expectedParameters.length
      && parameterNames.every((name) => expectedParameters.includes(name))
      && scopes.length === expectedScopes.size
      && new Set(scopes).size === scopes.length
      && scopes.every((scope) => expectedScopes.has(scope));
    return safe ? value : null;
  } catch {
    return null;
  }
}

function browserQuery(input: {
  query?: string;
  kind?: "GROUP" | "DIRECT" | "BROADCAST";
  cursor?: string;
  limit?: number;
}): string {
  const query = new URLSearchParams();
  if (input.query) query.set("q", input.query);
  if (input.kind) query.set("kind", input.kind);
  if (input.cursor) query.set("cursor", input.cursor);
  if (input.limit !== undefined) query.set("limit", String(input.limit));
  return query.toString();
}
